import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../styles/ReporteCargaAcademicaStyles/ReporteCargaAcademica.module.css';
import { getCycles } from '../../api/ciclosEscolares';
import { getSeccionesAcademicas } from '../../api/secciones_academicas';
import { getAsesores } from '../../api/teachers';
import { getEstudiantes } from '../../api/inscripcionReinscripcion';

/**
 * Filtros del Reporte de Carga Académica.
 *
 * Props:
 *  - filters: { tipo_reporte, id_ciclo_escolar, id_campus, id_seccion,
 *               id_asesor, id_estudiante, matricula }
 *  - onChange(key, value)
 *  - onGenerate()
 *  - loading: boolean
 *  - onContextResolved({ ciclo, campus, seccion, asesor, estudiante, matricula })
 */
function FiltrosCargaAcademica({ filters, onChange, onGenerate, loading, onContextResolved }) {
  const [ciclos, setCiclos] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [asesores, setAsesores] = useState([]);

  // Autocomplete matrícula
  const [matriculaInput, setMatriculaInput] = useState(filters.matricula || '');
  const [matriculaSuggestions, setMatriculaSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [estudianteSeleccionado, setEstudianteSeleccionado] = useState(null);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      getCycles().catch(() => []),
      getSeccionesAcademicas().catch(() => []),
      getAsesores().catch(() => []),
    ]).then(([cic, sec, ase]) => {
      if (!mounted) return;
      setCiclos(Array.isArray(cic) ? cic : []);
      setSecciones(Array.isArray(sec) ? sec : []);
      setAsesores(Array.isArray(ase) ? ase : []);
    });
    return () => { mounted = false; };
  }, []);

  // Cerrar lista al hacer click afuera
  useEffect(() => {
    function onClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Buscar matrículas con debounce (sólo cuando tipo=ESTUDIANTE)
  useEffect(() => {
    if (filters.tipo_reporte !== 'ESTUDIANTE') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!matriculaInput || matriculaInput.trim().length < 2) {
      setMatriculaSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await getEstudiantes({ search: matriculaInput.trim() });
        const list = Array.isArray(data) ? data : (data?.results || []);
        setMatriculaSuggestions(list.slice(0, 15));
        setShowSuggestions(true);
      } catch (_) {
        setMatriculaSuggestions([]);
      }
    }, 300);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [matriculaInput, filters.tipo_reporte]);

  // Cambio de tipo reset campos del otro lado
  const handleTipoChange = (tipo) => {
    if (filters.tipo_reporte === tipo) return;
    onChange('tipo_reporte', tipo);
    if (tipo === 'ASESOR') {
      onChange('id_estudiante', '');
      onChange('matricula', '');
      setMatriculaInput('');
      setEstudianteSeleccionado(null);
    } else {
      onChange('id_asesor', '');
    }
  };

  const handleMatriculaPick = (est) => {
    const matricula = est.matricula || est.matricula_estudiante || '';
    setMatriculaInput(matricula);
    setEstudianteSeleccionado(est);
    onChange('id_estudiante', est.id_estudiante || est.id || '');
    onChange('matricula', matricula);
    setShowSuggestions(false);
  };

  const handleMatriculaInputChange = (val) => {
    setMatriculaInput(val);
    onChange('matricula', val);
    // Si edita manualmente, se descarta selección previa
    if (estudianteSeleccionado) {
      setEstudianteSeleccionado(null);
      onChange('id_estudiante', '');
    }
  };

  const resolveContext = () => {
    const ciclo = ciclos.find((c) => String(c.id) === String(filters.id_ciclo_escolar));
    const seccion = secciones.find((s) => String(s.id) === String(filters.id_seccion));
    const asesor = asesores.find((a) => String(a.id_asesor || a.id) === String(filters.id_asesor));
    return {
      tipo_reporte: filters.tipo_reporte,
      ciclo: ciclo ? (ciclo.name || ciclo.nombre_ciclo) : '—',
      seccion: seccion ? seccion.nombre : 'Todas',
      asesor: asesor ? (asesor.nombre_completo || asesor.nombre) : (filters.tipo_reporte === 'ASESOR' ? 'Todos' : '—'),
      estudiante: estudianteSeleccionado
        ? (estudianteSeleccionado.nombre_completo || estudianteSeleccionado.nombre || '')
        : (filters.tipo_reporte === 'ESTUDIANTE' ? 'Todos' : '—'),
      matricula: filters.matricula || (filters.tipo_reporte === 'ESTUDIANTE' ? 'Todas' : '—'),
    };
  };

  const handleGenerate = () => {
    if (typeof onContextResolved === 'function') {
      onContextResolved(resolveContext());
    }
    onGenerate();
  };

  const disabled = loading || !filters.id_ciclo_escolar || !filters.tipo_reporte;
  const isAsesor = filters.tipo_reporte === 'ASESOR';
  const isEstudiante = filters.tipo_reporte === 'ESTUDIANTE';

  const asesoresOrdenados = useMemo(
    () => [...asesores].sort((a, b) => (a.nombre_completo || '').localeCompare(b.nombre_completo || '')),
    [asesores]
  );

  return (
    <section className={styles.filtersSection}>
      {/* Toggle ASESOR / ESTUDIANTE */}
      <div className={styles.filtersRow} style={{ marginBottom: 12 }}>
        <div className={styles.filterGroup} style={{ flex: '0 0 auto', minWidth: 'auto' }}>
          <label className={styles.filterLabel}>Tipo de reporte *</label>
          <div className={styles.typeToggle}>
            <button
              type="button"
              className={`${styles.typeToggleButton} ${isAsesor ? styles.typeToggleButtonActive : ''}`}
              onClick={() => handleTipoChange('ASESOR')}
            >
              ASESOR
            </button>
            <button
              type="button"
              className={`${styles.typeToggleButton} ${isEstudiante ? styles.typeToggleButtonActive : ''}`}
              onClick={() => handleTipoChange('ESTUDIANTE')}
            >
              ESTUDIANTE
            </button>
          </div>
        </div>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Ciclo Escolar *</label>
          <select
            className={styles.filterSelect}
            value={filters.id_ciclo_escolar || ''}
            onChange={(e) => onChange('id_ciclo_escolar', e.target.value)}
          >
            <option value="">Selecciona un ciclo</option>
            {ciclos.map((c) => (
              <option key={c.id} value={c.id}>{c.name || c.nombre_ciclo}</option>
            ))}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Sección</label>
          <select
            className={styles.filterSelect}
            value={filters.id_seccion || ''}
            onChange={(e) => onChange('id_seccion', e.target.value)}
          >
            <option value="">Todas</option>
            {secciones.map((s) => (
              <option key={s.id} value={s.id}>{s.nombre}</option>
            ))}
          </select>
        </div>

        {/* Filtro condicional ASESOR */}
        {isAsesor && (
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Asesor</label>
            <select
              className={styles.filterSelect}
              value={filters.id_asesor || ''}
              onChange={(e) => onChange('id_asesor', e.target.value)}
            >
              <option value="">Todos</option>
              {asesoresOrdenados.map((a) => (
                <option key={a.id_asesor || a.id} value={a.id_asesor || a.id}>
                  {a.nombre_completo || a.nombre || `Asesor #${a.id_asesor || a.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Filtro condicional ESTUDIANTE (matrícula con autocomplete) */}
        {isEstudiante && (
          <div className={styles.filterGroup} ref={wrapperRef}>
            <label className={styles.filterLabel}>Matrícula</label>
            <div className={styles.autocompleteWrapper}>
              <input
                type="text"
                className={styles.filterInput}
                placeholder="Buscar matrícula o nombre..."
                value={matriculaInput}
                onChange={(e) => handleMatriculaInputChange(e.target.value)}
                onFocus={() => matriculaSuggestions.length > 0 && setShowSuggestions(true)}
                autoComplete="off"
                style={{ width: '100%' }}
              />
              {showSuggestions && (
                <div className={styles.autocompleteList}>
                  {matriculaSuggestions.length === 0 ? (
                    <div className={styles.autocompleteEmpty}>Sin coincidencias.</div>
                  ) : (
                    matriculaSuggestions.map((est) => (
                      <div
                        key={est.id_estudiante || est.id}
                        className={styles.autocompleteItem}
                        onClick={() => handleMatriculaPick(est)}
                      >
                        <span className={styles.autocompleteMatricula}>{est.matricula || '—'}</span>
                        {est.nombre_completo || est.nombre || ''}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className={styles.filterGroup} style={{ flex: '0 0 auto', minWidth: 'auto' }}>
          <label className={styles.filterLabel}>&nbsp;</label>
          <button
            type="button"
            className={styles.generateButton}
            onClick={handleGenerate}
            disabled={disabled}
          >
            {loading ? 'Generando...' : 'Generar Reporte'}
          </button>
        </div>
      </div>
    </section>
  );
}

export default FiltrosCargaAcademica;
